/**
 * Fechas y horas civiles: "2026-08-01" y "23:30" tal como los dice una persona.
 *
 * Este modulo **no sabe nada de zonas horarias ni de instantes**. Es aritmetica de
 * calendario pura, con enteros, y sin usar `Date` en ningun momento. La conversion a
 * un instante real vive en `zona.ts`, que es el unico que conoce `America/Montevideo`.
 *
 * No usar `Date` aca no es purismo: `new Date('2026-08-01')` y `new Date('2026-8-1')`
 * se interpretan distinto, y sumar un mes con `setMonth` produce el 3 de marzo cuando
 * partis del 31 de enero. La aritmetica entera no tiene ninguna de esas sorpresas.
 */
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
const PATRON_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;
const PATRON_HORA = /^(\d{2}):(\d{2})$/;
/** El anio es bisiesto en el calendario gregoriano proleptico. */
export function esBisiesto(anio) {
    return (anio % 4 === 0 && anio % 100 !== 0) || anio % 400 === 0;
}
/** Cantidad de dias del mes. `mes` de 1 a 12. */
export function diasDelMes(anio, mes) {
    const dias = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (mes === 2 && esBisiesto(anio)) {
        return 29;
    }
    return dias[mes - 1] ?? 0;
}
/** Construye una fecha validando que exista de verdad (el 30 de febrero no pasa). */
export function crearFechaLocal(anio, mes, dia, campo) {
    const enteros = Number.isInteger(anio) && Number.isInteger(mes) && Number.isInteger(dia);
    if (!enteros || anio < 1000 || anio > 9999 || mes < 1 || mes > 12) {
        return fallo(errorDominio('FECHA_LOCAL_INVALIDA', campo));
    }
    if (dia < 1 || dia > diasDelMes(anio, mes)) {
        return fallo(errorDominio('FECHA_LOCAL_INVALIDA', campo));
    }
    return exito({ anio, mes, dia });
}
/** Construye una hora validando el rango. */
export function crearHoraLocal(hora, minuto, campo) {
    const enteros = Number.isInteger(hora) && Number.isInteger(minuto);
    if (!enteros || hora < 0 || hora > 23 || minuto < 0 || minuto > 59) {
        return fallo(errorDominio('HORA_LOCAL_INVALIDA', campo));
    }
    return exito({ hora, minuto });
}
/** Lee una fecha en formato `YYYY-MM-DD`, el que se persiste (docs/MODELO_DATOS.md). */
export function fechaLocalDesdeTexto(texto, campo) {
    const partes = PATRON_FECHA.exec(texto);
    if (partes === null) {
        return fallo(errorDominio('FECHA_LOCAL_INVALIDA', campo));
    }
    return crearFechaLocal(Number(partes[1]), Number(partes[2]), Number(partes[3]), campo);
}
/** Lee una hora en formato `HH:mm`. */
export function horaLocalDesdeTexto(texto, campo) {
    const partes = PATRON_HORA.exec(texto);
    if (partes === null) {
        return fallo(errorDominio('HORA_LOCAL_INVALIDA', campo));
    }
    return crearHoraLocal(Number(partes[1]), Number(partes[2]), campo);
}
/** Escribe la fecha como `YYYY-MM-DD`. */
export function textoDeFechaLocal(fecha) {
    const anio = String(fecha.anio).padStart(4, '0');
    const mes = String(fecha.mes).padStart(2, '0');
    const dia = String(fecha.dia).padStart(2, '0');
    return `${anio}-${mes}-${dia}`;
}
/** Escribe la hora como `HH:mm`. */
export function textoDeHoraLocal(hora) {
    return `${String(hora.hora).padStart(2, '0')}:${String(hora.minuto).padStart(2, '0')}`;
}
/**
 * Dias transcurridos desde 1970-01-01 en el calendario gregoriano proleptico.
 *
 * Algoritmo de Howard Hinnant (`days_from_civil`): aritmetica entera exacta, sin tablas
 * ni bucles, valido para cualquier anio. Es la base de toda la aritmetica de calendario
 * de este modulo, y por eso los cambios de mes, de anio y los bisiestos salen bien sin
 * ningun caso especial.
 */
export function diasDesdeEpoca(fecha) {
    const anio = fecha.mes <= 2 ? fecha.anio - 1 : fecha.anio;
    const era = Math.floor(anio / 400);
    const anioDeEra = anio - era * 400;
    const diaDeAnio = Math.floor((153 * (fecha.mes + (fecha.mes > 2 ? -3 : 9)) + 2) / 5) + fecha.dia - 1;
    const diaDeEra = anioDeEra * 365 + Math.floor(anioDeEra / 4) - Math.floor(anioDeEra / 100) + diaDeAnio;
    return era * 146097 + diaDeEra - 719468;
}
/** Inversa de `diasDesdeEpoca`. */
export function fechaDesdeDias(dias) {
    const desplazado = dias + 719468;
    const era = Math.floor(desplazado / 146097);
    const diaDeEra = desplazado - era * 146097;
    const anioDeEra = Math.floor((diaDeEra -
        Math.floor(diaDeEra / 1460) +
        Math.floor(diaDeEra / 36524) -
        Math.floor(diaDeEra / 146096)) /
        365);
    const anio = anioDeEra + era * 400;
    const diaDeAnio = diaDeEra - (365 * anioDeEra + Math.floor(anioDeEra / 4) - Math.floor(anioDeEra / 100));
    const mesDesplazado = Math.floor((5 * diaDeAnio + 2) / 153);
    const dia = diaDeAnio - Math.floor((153 * mesDesplazado + 2) / 5) + 1;
    const mes = mesDesplazado + (mesDesplazado < 10 ? 3 : -9);
    return { anio: mes <= 2 ? anio + 1 : anio, mes, dia };
}
/**
 * Suma dias a una fecha. Cruza meses, anios y bisiestos correctamente porque opera
 * sobre el numero de dia, no sobre los campos.
 */
export function sumarDiasLocales(fecha, dias) {
    return fechaDesdeDias(diasDesdeEpoca(fecha) + dias);
}
/**
 * Suma meses de calendario a una fecha (ADR-080).
 *
 * Misma aritmetica que `desplazarVista('mes', …)`: se mueve el indice del mes y el dia
 * **se fija al ultimo del mes destino** cuando no existe en el. Asi, 31/01 + 1 da 28/02
 * (o 29/02 en bisiesto), y nunca el 3 de marzo que produce `setMonth` o sumar dias.
 *
 * `meses` puede ser negativo. Un valor no entero es una violacion de invariante.
 */
export function sumarMesesCalendario(fecha, meses) {
    if (!Number.isSafeInteger(meses)) {
        throw new RangeError('sumarMesesCalendario: la cantidad de meses debe ser entera.');
    }
    const indiceMes = fecha.anio * 12 + (fecha.mes - 1) + meses;
    const anio = Math.floor(indiceMes / 12);
    const mes = indiceMes - anio * 12 + 1;
    return { anio, mes, dia: Math.min(fecha.dia, diasDelMes(anio, mes)) };
}
/** Dias entre dos fechas: `hasta - desde`. Negativo si `hasta` es anterior. */
export function diferenciaEnDiasLocales(desde, hasta) {
    return diasDesdeEpoca(hasta) - diasDesdeEpoca(desde);
}
/** Compara dos fechas: -1, 0 o 1. */
export function compararFechasLocales(a, b) {
    const diferencia = diferenciaEnDiasLocales(b, a);
    if (diferencia < 0)
        return -1;
    if (diferencia > 0)
        return 1;
    return 0;
}
/** Minutos desde la medianoche local. */
export function minutosDesdeMedianoche(hora) {
    return hora.hora * 60 + hora.minuto;
}
/** Compara dos horas: -1, 0 o 1. */
export function compararHorasLocales(a, b) {
    const diferencia = minutosDesdeMedianoche(a) - minutosDesdeMedianoche(b);
    if (diferencia < 0)
        return -1;
    if (diferencia > 0)
        return 1;
    return 0;
}
/** Dos fechas y horas civiles son el mismo valor. */
export function sonIgualesFechaHora(a, b) {
    return (a.fecha.anio === b.fecha.anio &&
        a.fecha.mes === b.fecha.mes &&
        a.fecha.dia === b.fecha.dia &&
        a.hora.hora === b.hora.hora &&
        a.hora.minuto === b.hora.minuto);
}
//# sourceMappingURL=civil.js.map